import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import ViewListIcon from '@mui/icons-material/ViewList';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import { printPalletLabels } from '../../../store/store';
import CopyLinkButton from '../../main/copyLinkButton';
import { useDocFormat, useDocLanguage, stoneSort } from '../docFormat';
import { readPaper, writePaper } from './shortLabelSheetDialog';
import { kindInfo, useLabelPreview, LabelPreview, LabelKindSelect, LabelLanguageSelect, LabelPaperToggle } from './labelPrintControls';

const PHONE_ICON = { width: 40, height: 40 };
const ITEM_COLS = '30px minmax(120px, 1.6fr) 50px repeat(4, minmax(46px, 0.6fr)) minmax(84px, 0.9fr)';
const ITEM_COLS_XS = '20px minmax(0, 1.7fr) minmax(0, 0.5fr) repeat(4, minmax(0, 0.68fr)) minmax(0, 1.3fr)';

function Meta({ label, value, T, mono }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: T.TEXT_TER, mb: 0.15 }}>{label}</Typography>
      <Typography dir={mono ? 'ltr' : undefined} sx={{ fontSize: '0.78rem', color: T.TEXT_PRI, textAlign: 'start', fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-word' }}>{value}</Typography>
    </Box>
  );
}

// A pallet's record: the same shell as a packing list's and an invoice's. It opens as the DOCUMENT, the label as it will print, with its print options in the header;
// the Data view holds what the label is made from.
export default function PalletLabelDetail({ palletId, initialRow, branchId, refreshKey, canPrint, canEdit, canDelete, colors, calendar, onBack, onEdit, onDelete, onOpenList, T }) {
  const { t } = useTranslation();
  const fmt = useDocFormat();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);

  const [row, setRow] = useState(null);
  const [missing, setMissing] = useState(false);
  const [view, setView] = useState('document');      // 'document' | 'data'
  const [kind, setKind] = useState('slab');
  const [paper, setPaper] = useState(readPaper);
  const [printing, setPrinting] = useState(false);
  const [lang, setLang] = useDocLanguage(palletId);

  useEffect(() => {
    let cancelled = false;
    setRow((cur) => (cur && cur._id === palletId ? cur : (initialRow && initialRow._id === palletId ? initialRow : null))); setMissing(false);
    authCtx.jwtInst({ method: 'get', url: `${axiosGlobal.defaultTargetApi}/mis/pallets/${palletId}` })
      .then((res) => { if (!cancelled) setRow(res.data); })
      .catch(() => { if (!cancelled) setMissing(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [palletId, refreshKey, authCtx, axiosGlobal]);

  // the label is asked for at the same time as the record, not after it: the branch is the screen's
  const ids = useMemo(() => (palletId ? [palletId] : []), [palletId]);
  const preview = useLabelPreview({ branchId: branchId || (row && row.branchId), ids, kind, lang, paper, refreshKey, enabled: view === 'document' });
  const info = kindInfo(kind);

  if (missing) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><Typography sx={{ fontSize: '0.8rem', color: T.TEXT_SEC }}>{t('mis.lblNotFound', { codes: '' })}</Typography></Box>;
  }
  if (!row || row._id !== palletId) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={20} /></Box>;

  const items = row.items || [];
  const pcs = items.reduce((a, it) => a + (Number(it.pcs) || 0), 0);
  const sqm = items.reduce((a, it) => a + (Number(it.sqm) || 0), 0);
  const final = Boolean(row.list && row.list.status === 'final');
  const stateColor = colors[!row.list ? 'available' : final ? 'final' : 'onList'];
  const stateLabel = !row.list ? t('mis.lblAvailable') : t(final ? 'mis.lblFinalChip' : 'mis.lblOnListChip', { list: row.list.docNumber });

  const print = async () => {
    setPrinting(true);
    await dispatch(printPalletLabels({ authCtx, axiosGlobal, branchId: row.branchId, ids: [row._id], kind, lang, paper: info.usesPaper ? paper : undefined, html: preview.html }));
    setPrinting(false);
  };
  const pickPaper = (value) => { setPaper(value); writePaper(value); };

  const meta = [
    [t('mis.referenceField'), row.reference, true],
    [t('mis.palletIdField'), row.palletId],
    [t('mis.processingTypeField'), row.processingType],
    [t('mis.lblPalletizedBy'), row.palletizedBy],
    [t('mis.destinationAddressField'), row.destinationAddress],
    [t('mis.lblCreatedBy'), row.createdByName],
    [t('mis.lblCreatedOn'), fmt.date(row.insertDate, calendar)],
  ].filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');
  const cols = isXs ? ITEM_COLS_XS : ITEM_COLS;
  const numberCell = { fontSize: '0.73rem', textAlign: 'end', fontVariantNumeric: 'tabular-nums', color: T.TEXT_SEC, minWidth: 0 };

  return (
    <Box data-testid="lbl-detail" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${T.BD}`, flexShrink: 0, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={onBack} aria-label={t('common.back')} sx={isXs ? PHONE_ICON : undefined}>
          <ArrowBackIcon sx={{ fontSize: isXs ? 22 : 18, color: T.TEXT_SEC }} />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
            <Typography data-testid="lbl-detail-title" sx={{ fontWeight: 700, fontSize: '0.95rem', color: T.TEXT_PRI }}>
              {t('mis.lblDetailTitle', { code: row.reference })}
            </Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.75, py: '1px', borderRadius: '5px', bgcolor: `${stateColor}1f` }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: stateColor }} />
              <Typography data-testid="lbl-detail-state" sx={{ fontSize: '0.62rem', fontWeight: 700, color: stateColor, whiteSpace: 'nowrap' }}>{stateLabel}</Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC }}>
            {[fmt.date(row.insertDate, calendar), t('mis.plTotalsSummary', { pcs, sqm: sqm.toFixed(4) })].join(' · ')}
          </Typography>
        </Box>

        <Box data-testid="lbl-print-options" sx={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: isXs ? 0.5 : 0.75, ...(isXs ? { width: '100%', marginInlineStart: 0 } : {}) }}>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, val) => val && setView(val)}>
            <ToggleButton value="document" aria-label={t('mis.plViewDocument')} data-testid="lbl-view-document" sx={{ px: 1, py: 0.25, ...(isXs ? PHONE_ICON : {}) }}>
              <Tooltip title={t('mis.plViewDocument')}><ArticleOutlinedIcon sx={{ fontSize: isXs ? 19 : 15 }} /></Tooltip>
            </ToggleButton>
            <ToggleButton value="data" aria-label={t('mis.plViewData')} data-testid="lbl-view-data" sx={{ px: 1, py: 0.25, ...(isXs ? PHONE_ICON : {}) }}>
              <Tooltip title={t('mis.plViewData')}><ViewListIcon sx={{ fontSize: isXs ? 19 : 15 }} /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <LabelKindSelect value={kind} onChange={setKind} count={1} touch={isXs} sx={isXs ? { flex: 1 } : undefined} />
          {info.usesLanguage && <LabelLanguageSelect value={lang} onChange={setLang} touch={isXs} />}
          {info.usesPaper && <LabelPaperToggle value={paper} onChange={pickPaper} touch={isXs} />}
          {canPrint && (isXs ? (
            <Tooltip title={t('mis.lblPrintOne')}>
              <span>
                <IconButton onClick={print} disabled={printing} aria-label={t('mis.lblPrintOne')} data-testid="lbl-detail-print" sx={PHONE_ICON}>
                  {printing ? <CircularProgress size={16} /> : <PrintIcon sx={{ fontSize: 20, color: T.TEXT_SEC }} />}
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Button size="small" variant="contained" onClick={print} disabled={printing} data-testid="lbl-detail-print"
              startIcon={printing ? <CircularProgress size={12} color="inherit" /> : <PrintIcon sx={{ fontSize: 14 }} />}
              sx={{ fontSize: '0.7rem', textTransform: 'none', height: 28 }}>
              {t('mis.lblPrintOne')}
            </Button>
          ))}
          <CopyLinkButton module="mis" entityType="pallet" entityId={row._id} sx={isXs ? { ...PHONE_ICON, '& svg': { fontSize: 19 } } : undefined} />
          {canEdit && !final && (
            <Tooltip title={t('common.edit')}>
              <IconButton size="small" onClick={() => onEdit(row)} aria-label={t('common.edit')} data-testid="lbl-edit" sx={isXs ? PHONE_ICON : undefined}>
                <EditIcon sx={{ fontSize: isXs ? 19 : 16, color: T.TEXT_SEC }} />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && !row.list && (
            <Tooltip title={t('mis.lblDelete')}>
              <IconButton size="small" onClick={() => onDelete(row)} aria-label={t('mis.lblDelete')} data-testid="lbl-delete" sx={isXs ? PHONE_ICON : undefined}>
                <DeleteOutlineIcon sx={{ fontSize: isXs ? 19 : 16, color: T.TEXT_SEC }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {row.list && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderBottom: `1px solid ${T.BD}`, flexShrink: 0, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.72rem', color: T.TEXT_SEC, flex: 1, minWidth: 0 }}>
            {final ? t('mis.lblLockedNote', { list: row.list.docNumber }) : t('mis.lblOnDraftNote', { list: row.list.docNumber })}
          </Typography>
          <Button size="small" onClick={() => onOpenList(row.list._id)} data-testid="lbl-open-list" startIcon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '0.7rem', textTransform: 'none', minHeight: { xs: 40, sm: 0 } }}>
            {t('mis.lblOpenList')}
          </Button>
        </Box>
      )}

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 2 }}>
        {view === 'document' ? (
          <LabelPreview html={preview.html} loading={preview.loading} kind={kind} paper={paper} title={`label-${row.reference}`} T={T} />
        ) : (
          <>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: isXs ? '1fr 1fr' : 'repeat(auto-fill, minmax(190px, 1fr))',
              p: 1.5, mb: 2, border: `1px solid ${T.BD}`, borderRadius: '10px', bgcolor: T.CTRL_BG }}>
              {meta.map(([label, value, mono]) => <Meta key={label} label={label} value={value} mono={mono} T={T} />)}
            </Box>
            <Box sx={{ border: `1px solid ${T.BD}`, borderRadius: '12px', px: 1.5, py: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: isXs ? 0.75 : 1, pb: 0.5, alignItems: 'end', borderBottom: `1px solid ${T.BD}` }}>
                {[
                  { l: t('mis.plNoField') }, { l: t('mis.plColCode') }, { l: t('mis.plColSort') },
                  { l: t('mis.plColLength'), end: true }, { l: t('mis.plColWidth'), end: true }, { l: t('mis.plColThickness'), end: true },
                  { l: t('mis.plColPcs'), end: true }, { l: t('mis.plColSqm'), end: true },
                ].map((c) => (
                  <Typography key={c.l} sx={{ minWidth: 0, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: isXs ? 0.1 : 0.4, color: T.TEXT_TER, textAlign: c.end ? 'end' : 'start', overflowWrap: 'break-word' }}>
                    {c.l}
                  </Typography>
                ))}
              </Box>
              {items.map((it, i) => (
                <Box key={i} data-lbl-row={i} sx={{ display: 'grid', gridTemplateColumns: cols, gap: isXs ? 0.75 : 1, py: 0.55, bgcolor: i % 2 ? T.ROW_ALT : 'transparent', borderRadius: '6px' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: T.TEXT_TER, minWidth: 0 }}>{i + 1}</Typography>
                  <Typography data-lbl-cell="code" dir="ltr" sx={{ fontFamily: 'monospace', fontSize: '0.73rem', color: T.TEXT_PRI, textAlign: 'start', minWidth: 0, wordBreak: 'break-all' }}>{it.code || '—'}</Typography>
                  <Typography data-lbl-cell="sort" sx={{ fontSize: '0.73rem', fontWeight: 600, color: T.TEXT_PRI, minWidth: 0 }}>{stoneSort(it.sort) || '—'}</Typography>
                  <Typography data-lbl-cell="l" sx={numberCell}>{it.lengthCm ?? '—'}</Typography>
                  <Typography data-lbl-cell="w" sx={numberCell}>{it.widthCm ?? '—'}</Typography>
                  <Typography data-lbl-cell="t" sx={numberCell}>{it.thicknessCm ?? '—'}</Typography>
                  <Typography data-lbl-cell="pcs" sx={numberCell}>{it.pcs ?? '—'}</Typography>
                  <Typography data-lbl-cell="sqm" sx={{ ...numberCell, fontWeight: 600, color: T.TEXT_PRI }}>{it.sqm != null ? Number(it.sqm).toFixed(4) : '—'}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 0.75, mt: 0.5, borderTop: `1px solid ${T.BD}` }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: T.TEXT_PRI }}>{pcs} {t('mis.plColPcs')}</Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: T.TEXT_PRI }}>{sqm.toFixed(4)} {t('mis.plColSqm')}</Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
